import { translations, interpolate } from './translations/index';

export type Language = 'ro' | 'en' | 'de' | 'fr' | 'es' | 'it';
export type TranslationKey = keyof typeof translations.ro;

/**
 * Get translation for a specific key and language
 * @param key - Translation key
 * @param lang - Language code
 * @param params - Parameters for string interpolation
 * @param fallback - Fallback text if translation not found
 * @returns Translated string
 */
export const t = (
  key: TranslationKey, 
  lang: Language = 'ro', 
  params: Record<string, string | number> = {}, 
  fallback?: string
): string => {
  const translation = translations[lang]?.[key] || translations.ro[key] || fallback || key;
  return interpolate(translation, params);
};

/**
 * Create a translation function bound to a specific language
 * @param lang - Language code
 * @returns Translation function
 */
export const createTranslator = (lang: Language) => {
  return (key: TranslationKey, params: Record<string, string | number> = {}, fallback?: string): string => {
    return t(key, lang, params, fallback);
  };
};

/**
 * Get all available languages
 * @returns Array of language codes
 */
export const getAvailableLanguages = (): Language[] => {
  return Object.keys(translations) as Language[];
};

/**
 * Check if a language is supported
 * @param lang - Language code to check
 * @returns True if language is supported
 */
export const isLanguageSupported = (lang: string): lang is Language => {
  return getAvailableLanguages().includes(lang as Language);
};
